<template>
  <v-dialog :value="value" @input="$emit('input', $event)" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="700" scrollable>
    <v-card v-if="product" dark class="product-dialog-card">
      <v-btn icon class="close-btn" @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <div class="image-carousel-wrap">
        <v-carousel
          v-if="imageUrls.length > 0"
          v-model="carouselIndex"
          height="380"
          hide-delimiter-background
          :show-arrows="imageUrls.length > 1"
          :hide-delimiters="imageUrls.length <= 1"
          dark>
          <v-carousel-item v-for="(url, idx) in imageUrls" :key="idx">
            <div class="carousel-image-wrap">
              <img :src="url" :alt="product.title" class="carousel-image" />
            </div>
          </v-carousel-item>
        </v-carousel>
        <div v-else class="no-image-placeholder">
          <v-icon size="80" color="grey darken-1">
            {{ isDigital ? "mdi-file-pdf-box" : "mdi-image-off" }}
          </v-icon>
        </div>

        <v-chip v-if="isDigital" small color="primary" dark class="type-chip-overlay">
          <v-icon left x-small>mdi-download</v-icon>
          Download
        </v-chip>
      </div>

      <v-card-text class="pa-6">
        <h2 class="product-title">{{ product.title }}</h2>

        <div class="product-quantity-info caption grey--text mt-1">
          <span v-if="isDigital">PDF-Download</span>
          <span v-else>Menge: {{ product.minQuantity }} – {{ product.maxQuantity }} Stk</span>
        </div>

        <div v-if="product.description" class="product-description mt-4">{{ product.description }}</div>

        <!-- Optionen / Auswahlfelder -->
        <div v-if="product.options && product.options.length > 0" class="options-selection mt-4">
          <v-row dense>
            <v-col cols="12" v-for="opt in product.options" :key="opt._id || opt.name" class="mb-2">
              <v-select
                v-model="selectedOptionsMap[opt.name]"
                :items="opt.choices"
                item-text="value"
                item-value="value"
                :label="opt.name + ' *'"
                outlined
                dense
                dark
                hide-details
                @change="handleOptionChange(opt, $event)"
              />
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-6" dark></v-divider>

        <div v-if="!isDigital" class="qty-selector">
          <div class="qty-label">Anzahl</div>
          <div class="qty-stepper-large">
            <v-btn icon @click="decreaseQty" :disabled="qty <= product.minQuantity">
              <v-icon>mdi-minus</v-icon>
            </v-btn>
            <input
              v-model.number="qty"
              type="number"
              class="qty-input-large"
              :min="product.minQuantity"
              :max="product.maxQuantity"
              @blur="clampQty" />
            <v-btn icon @click="increaseQty" :disabled="qty >= product.maxQuantity">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-else class="digital-info">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          <span class="caption"> Dieses Dokument wird nach dem Bestellen sofort heruntergeladen und zusätzlich per Mail zugestellt. </span>
        </div>

        <div v-if="cartQuantity > 0" class="caption mt-3" style="color: #009640">
          <v-icon small color="success">mdi-cart-check</v-icon>
          <span v-if="isDigital">Bereits im Warenkorb</span>
          <span v-else>Du hast bereits {{ cartQuantity }} Stk im Warenkorb</span>
        </div>
      </v-card-text>

      <v-divider dark></v-divider>

      <v-card-actions class="pa-4">
        <v-btn text @click="$emit('input', false)">Schliessen</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" depressed rounded large @click="addToCart" :disabled="!allOptionsSelected">
          <v-icon left>{{ isDigital ? "mdi-cart-arrow-down" : "mdi-cart-plus" }}</v-icon>
          {{ isDigital ? "Zum Warenkorb" : (allOptionsSelected ? `${qty} zum Warenkorb` : 'Bitte Optionen wählen') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "ProductDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    product: { type: Object, default: null },
    cartQuantity: { type: Number, default: 0 },
  },
  data() {
    return { qty: 1, imageUrls: [], selectedOptionsMap: {}, carouselIndex: 0 }
  },
  computed: {
    isDigital() {
      return this.product && this.product.productType === "digital"
    },
    allOptionsSelected() {
      if (!this.product || !this.product.options || this.product.options.length === 0) return true
      return this.product.options.every((o) => !!this.selectedOptionsMap[o.name])
    },
  },
  watch: {
    value(open) {
      if (open && this.product) {
        this.qty = this.product.minQuantity || 1
        this.selectedOptionsMap = {}
        this.carouselIndex = 0
        this.loadImages()
      } else if (!open) {
        this.cleanupImages()
      }
    },
  },
  methods: {
    async loadImages() {
      this.cleanupImages()
      if (!this.product || !this.product.images) return
      const urls = []
      for (const img of this.product.images) {
        try {
          const url = await api.marketingShop.loadProductImage(this.product._id, img.fileId)
          urls.push(url)
        } catch (e) {
          console.error("Bild konnte nicht geladen werden:", e)
        }
      }
      this.imageUrls = urls
    },
    cleanupImages() {
      this.imageUrls.forEach((u) => URL.revokeObjectURL(u))
      this.imageUrls = []
    },
    handleOptionChange(opt, value) {
      const choice = (opt.choices || []).find((c) => c.value === value)
      if (choice && choice.imageFileId && this.product.images) {
        const idx = this.product.images.findIndex((img) => img.fileId === choice.imageFileId)
        if (idx !== -1) {
          this.carouselIndex = idx
        }
      }
    },
    increaseQty() {
      this.qty = Math.min((this.qty || 1) + 1, this.product.maxQuantity || 100)
    },
    decreaseQty() {
      this.qty = Math.max((this.qty || 1) - 1, this.product.minQuantity || 1)
    },
    clampQty() {
      const min = this.product.minQuantity || 1
      const max = this.product.maxQuantity || 100
      let v = parseInt(this.qty) || min
      v = Math.max(min, Math.min(max, v))
      this.qty = v
    },
    addToCart() {
      if (!this.allOptionsSelected) return
      if (this.isDigital) {
        this.$emit("add-to-cart", { product: this.product, quantity: 1, selectedOptions: { ...this.selectedOptionsMap } })
      } else {
        this.clampQty()
        this.$emit("add-to-cart", { product: this.product, quantity: this.qty, selectedOptions: { ...this.selectedOptionsMap } })
      }
      this.$emit("input", false)
    },
  },
  beforeDestroy() {
    this.cleanupImages()
  },
}
</script>

<style scoped>
.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5) !important;
}

.image-carousel-wrap {
  position: relative;
  background: rgba(255, 255, 255, 0.03);
}

.type-chip-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
}

.carousel-image-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.no-image-placeholder {
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-title {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  margin: 0;
}

.product-description {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  white-space: pre-wrap;
}

.qty-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qty-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.qty-stepper-large {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 4px;
}

.qty-input-large {
  width: 60px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input-large::-webkit-outer-spin-button,
.qty-input-large::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.digital-info {
  display: flex;
  align-items: flex-start;
  background: rgba(0, 150, 64, 0.08);
  border-left: 3px solid #009640;
  padding: 12px 14px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.8);
}
</style>
